"use strict";
/**
 * Copyright © 2019 Johnson & Johnson
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 * http://www.apache.org/licenses/LICENSE-2.0
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) {
        if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
    }

    return t;
  };

  return __assign.apply(this, arguments);
};

var __importDefault = this && this.__importDefault || function (mod) {
  return mod && mod.__esModule ? mod : {
    "default": mod
  };
};

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useGetItemUseGetMenuOptions = exports.useMenuOptions = void 0;

var react_1 = require("react");

var lodash_1 = require("lodash");

var core_1 = require("@bodiless/core");

var model_1 = require("./model");

var componentSelectorForm_1 = __importDefault(require("../ComponentSelector/componentSelectorForm"));

var SortableChild_1 = require("./SortableChild");

var appendTailwindWidthClass_1 = require("./utils/appendTailwindWidthClass");
/**
 * @private
 *
 * Removes components from the design which are part of the actual flow container design,
 * not intended to appear as options in the component selector.
 *
 * @param props The original props of the flow container.
 *
 * @return The props with irrelevant components removed.
 */


var withNoDesign = function withNoDesign(props) {
  return __assign(__assign({}, props), {
    components: lodash_1.omit(props.components, ['Wrapper', 'ComponentWrapper'])
  });
};
/**
 * @private
 *
 * Returns actions which can be executed upon selecting a component in the
 * component selector.
 *
 * @param props The props provided to the FlowContainer
 * @param currentItem The currently selected item in the grid (optional);
 */


var useComponentSelectorActions = function useComponentSelectorActions(handlers, props, currentItem) {
  var _a = props.getDefaultWidth,
      getDefaultWidth = _a === void 0 ? function () {
    return SortableChild_1.FALLBACK_SNAP_CLASSNAME;
  } : _a,
      _b = props.snapData,
      snapData = _b === void 0 ? appendTailwindWidthClass_1.defaultSnapData : _b;
  var insertFlowContainerItem = handlers.insertFlowContainerItem,
      updateFlowContainerItem = handlers.updateFlowContainerItem;
  var setId = core_1.useActivateOnEffect().setId;
  var wrapperProps = {
    className: getDefaultWidth(snapData)
  };

  var insertItem = function insertItem(_a) {
    var componentName = _a[0];
    var uuid = insertFlowContainerItem(componentName, currentItem, wrapperProps).uuid; // Set the new id so it will activate on creation.

    setId(uuid);
  };

  var replaceItem = function replaceItem(_a) {
    var componentName = _a[0];

    if (currentItem) {
      var newItem = __assign(__assign({}, currentItem), {
        type: componentName
      });

      updateFlowContainerItem(newItem);
    }
  };

  return {
    insertItem: insertItem,
    replaceItem: replaceItem
  };
};

var useCloneButton = function useCloneButton(handlers, props, item) {
  var context = core_1.useEditContext();
  var insertFlowContainerItem = handlers.insertFlowContainerItem,
      getItems = handlers.getItems;
  var _a = props.maxComponents,
      maxComponents = _a === void 0 ? Infinity : _a;
  var setId = core_1.useActivateOnEffect().setId;

  var handler = function handler() {
    var uuid = insertFlowContainerItem(item.type, item, item.wrapperProps).uuid;
    setId(uuid);
  };

  var isHidden = react_1.useCallback(function () {
    return !context.isEdit || getItems().length >= maxComponents;
  }, [maxComponents]);
  return {
    name: 'copy-item',
    label: 'Copy',
    icon: 'content_copy',
    global: false,
    local: true,
    handler: handler,
    isHidden: isHidden
  };
};

var useDeleteButton = function useDeleteButton(handlers, item) {
  var context = core_1.useEditContext();
  var deleteFlowContainerItem = handlers.deleteFlowContainerItem;
  var setId = core_1.useActivateOnEffect().setId;

  var handler = function handler() {
    var newContextItem = deleteFlowContainerItem(item.uuid); // Set the context to the next item in the flow container (if it exists)
    // or to the flow container itself (if not).

    if (newContextItem !== undefined) setId(newContextItem.uuid);else context.activate();
  };

  return {
    name: 'delete',
    label: 'Delete',
    icon: 'delete',
    global: false,
    local: true,
    handler: handler,
    isHidden: react_1.useCallback(function () {
      return !context.isEdit;
    }, [])
  };
};

var useAddButton = function useAddButton(handlers, props, item) {
  var _a = props.maxComponents,
      maxComponents = _a === void 0 ? Infinity : _a;
  var context = core_1.useEditContext();
  var insertItem = useComponentSelectorActions(handlers, props, item).insertItem;
  var getItems = handlers.getItems;
  var isHidden = item ? react_1.useCallback(function () {
    return !context.isEdit || getItems().length >= maxComponents;
  }, [maxComponents]) : react_1.useCallback(function () {
    return !context.isEdit || getItems().length > 0;
  }, []); // @TODO For nested flow containers we'll have to give these unique names.

  var name = item ? 'add-item' : 'add';
  return {
    icon: 'add',
    label: 'Add',
    global: false,
    local: true,
    name: name,
    handler: function handler() {
      return componentSelectorForm_1.default(props, insertItem);
    },
    activateContext: false,
    formTitle: 'Insert Component',
    isHidden: isHidden
  };
};

var useSwapButton = function useSwapButton(handlers, props, item) {
  var context = core_1.useEditContext();
  var replaceItem = useComponentSelectorActions(handlers, props, item).replaceItem;
  return {
    name: 'swap',
    label: 'Swap',
    icon: 'repeat',
    global: false,
    local: true,
    handler: function handler() {
      return componentSelectorForm_1.default(props, replaceItem);
    },
    activateContext: false,
    isHidden: react_1.useCallback(function () {
      return !context.isEdit;
    }, []),
    formTitle: 'Replace Component'
  };
};
/**
 * @private
 * Gets the context menu options for the flow container itself (an add button when the
 * flow container is empty).
 *
 * @param props The props passed to the flow container
 */


function useMenuOptions(props) {
  var handlers = __assign(__assign({}, model_1.useFlowContainerDataHandlers()), model_1.useItemHandlers());

  var addButton = useAddButton(handlers, withNoDesign(props));
  return [addButton];
}

exports.useMenuOptions = useMenuOptions;
/**
 * @private
 *
 * Returns a function which takes a flow container item and returns a 'useGetMenuOptions' hook
 * which is passed as a prop to that item. This hook is invoked in by the item to obtain a
 * memoized 'getMenuOptions' callback, which is in turn passed to PageContextProvider to define
 * the menu options for that item.
 *
 * The reasons for this extra indirection (a hook returning a hook) are:
 * - we want to build the menu options using the node of the flow container
 * - We want the options added using the context of the item
 * - The memoization of the options has to happen in the context of the item to avoid changing the
 *   order of hooks in the container as items are added and removed.
 * - The item must memoize the callback itself and use PageContextProvider directly
 *   rather than relying on `withMenuOptions`. This is because we don't want to call
 *   the `withMenuOptions` HOC in the context of a render.
 *
 * @param props Props passed to the flow container
 *
 * @return A function which generates a 'useGetMenuOptions' prop for an item.
 */

var useGetItemUseGetMenuOptions = function useGetItemUseGetMenuOptions(props) {
  // We have to obtain the handlers in the flow container context, and we have
  // to do it only once to avoid hook sequencing errors.
  var handlers = __assign(__assign({}, model_1.useFlowContainerDataHandlers()), model_1.useItemHandlers());

  var props$ = withNoDesign(props);
  return function (item) {
    return function () {
      var buttons = [// These hooks are all invoked by the flow container item (not the flow container itself).
      useAddButton(handlers, props$, item), useCloneButton(handlers, props$, item), useSwapButton(handlers, props$, item), useDeleteButton(handlers, item)];
      return core_1.useGetter(buttons);
    };
  };
};

exports.useGetItemUseGetMenuOptions = useGetItemUseGetMenuOptions;